import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuPencil, LuEye, LuTrash2, LuChevronLeft, LuChevronRight, LuInfo,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection, Switch } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import { ListRow, PerPage, RowAction, RowActions, ListCard, ListCards } from '../components/ListKit';
import { useCelular } from '../hooks/useCelular';
import Select from '../components/Select';
import '../components/ListKit.css';
import { useRecorte } from '../hooks/useRecorte';
import './PlanManagementPage.css';
import {
    billingService, rotuloProvedor, NOME_PROVEDOR,
    type PlanoBackend, type ProvedorPagamento,
} from '../services/billingService';

const POR_PAGINA = [25, 50, 100];
type Modo = 'ver' | 'editar';
type Filtro = 'todos' | 'ativos' | 'inativos';

const CICLOS: { valor: string; rotulo: string }[] = [
    { valor: 'WEEKLY', rotulo: 'Semanal' },
    { valor: 'MONTHLY', rotulo: 'Mensal' },
    { valor: 'QUARTERLY', rotulo: 'Trimestral' },
    { valor: 'SEMIANNUALLY', rotulo: 'Semestral' },
    { valor: 'ANNUALLY', rotulo: 'Anual' },
    { valor: 'TRIAL', rotulo: 'Teste' },
];

const rotuloCiclo = (c: string) => CICLOS.find(x => x.valor === c)?.rotulo ?? c;

const PROVEDORES = (Object.keys(NOME_PROVEDOR) as ProvedorPagamento[])
    .map(v => ({ value: v, label: NOME_PROVEDOR[v] }));

const brl = (v: number) =>
    (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const moedaDoTexto = (t: string) => Number(t.replace(/\D/g, '')) / 100;

/** `null` no limite significa ilimitado — não "zero". */
const limite = (v: number | null) => (v === null || v === undefined ? 'Ilimitado' : String(v));

interface Rascunho {
    planoId: number | null;
    nome: string;
    descricao: string;
    preco: number;
    ciclo: string;
    ativo: boolean;
    limiteConexao: number | null;
    integradoGateway: boolean;
    ehPlanoTeste: boolean;
    /* Escolhido só na criação. Na edição viaja no rascunho para ser devolvido
       intacto: `AtualizarPlano` tem default "ABACATEPAY" e trocaria o gateway de
       um plano Asaas em silêncio — e assinaturas ativas não acompanham a troca. */
    provedorPagamento: ProvedorPagamento;
}

const VAZIO: Rascunho = {
    planoId: null, nome: '', descricao: '', preco: 0, ciclo: 'MONTHLY', ativo: true,
    limiteConexao: null, integradoGateway: false, ehPlanoTeste: false,
    provedorPagamento: 'ABACATEPAY',
};

const PlanManagementPage = () => {
    const celular = useCelular();
    const [planos, setPlanos] = useState<PlanoBackend[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    /* Recorte de trabalho, na sessao da aba. */
    const [recorte, gravarRecorte] = useRecorte('pln:recorte', {
        busca: '', filtro: 'todos' as Filtro, pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [filtro, setFiltro] = useState<Filtro>(recorte.filtro);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, filtro, pagina, porPagina });
    }, [busca, filtro, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<PlanoBackend | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [rascunho, setRascunho] = useState<Rascunho>(VAZIO);
    const [precoTexto, setPrecoTexto] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erros, setErros] = useState<Record<string, string>>({});

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const r = await billingService.buscarTodosPlanos();
            if (r.sucesso) setPlanos(r.dados || []);
            else aviso.erro(r.mensagem || 'Não foi possível carregar os planos.');
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível carregar os planos.'));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);
    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, filtro, porPagina]);

    const contagens = useMemo(() => ({
        todos: planos.length,
        ativos: planos.filter(p => p.planoAtivo).length,
        inativos: planos.filter(p => !p.planoAtivo).length,
    }), [planos]);

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return planos.filter(p => {
            if (filtro === 'ativos' && !p.planoAtivo) return false;
            if (filtro === 'inativos' && p.planoAtivo) return false;
            if (!t) return true;
            return [p.planoNome, p.planoDescricao].some(v => (v || '').toLowerCase().includes(t));
        });
    }, [planos, busca, filtro]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    const abrirVer = (p: PlanoBackend) => { setAberto(p); setModo('ver'); setErros({}); };

    const abrirEdicao = (p: PlanoBackend) => {
        setAberto(p);
        setRascunho({
            planoId: p.planoId, nome: p.planoNome ?? '', descricao: p.planoDescricao ?? '',
            preco: p.planoPreco ?? 0, ciclo: p.planoCiclo, ativo: p.planoAtivo,
            limiteConexao: p.planoLimiteConexao,
            integradoGateway: p.planoIntegradoGateway, ehPlanoTeste: p.planoIsTrial,
            provedorPagamento: p.planoProvedorPagamento ?? 'ABACATEPAY',
        });
        setPrecoTexto(brl(p.planoPreco ?? 0));
        setModo('editar'); setErros({});
    };

    const abrirNovo = () => {
        setAberto({ planoId: 0 } as PlanoBackend);
        setRascunho(VAZIO); setPrecoTexto(''); setModo('editar'); setErros({});
    };

    const fechar = () => { setAberto(null); setErros({}); };
    const ehNovo = modo === 'editar' && rascunho.planoId === null;

    const salvar = async () => {
        const e: Record<string, string> = {};
        if (!rascunho.nome.trim()) e.nome = 'Informe o nome do plano.';
        if (Object.keys(e).length) { setErros(e); return; }

        setSalvando(true);
        try {
            if (rascunho.planoId) {
                /* `atualizarPlano` NÃO recebe `ciclo`: o ciclo de cobrança é
                   definido na criação e não muda depois. Por isso ele aparece
                   como leitura no modo edição — mostrar um select que a API
                   ignora seria mentir para quem edita. */
                const r = await billingService.atualizarPlano({
                    planoId: rascunho.planoId,
                    nome: rascunho.nome.trim(),
                    descricao: rascunho.descricao.trim(),
                    preco: rascunho.preco,
                    ativo: rascunho.ativo,
                    limiteConexao: rascunho.limiteConexao,
                    provedorPagamento: rascunho.provedorPagamento,
                });
                if (!r.sucesso) throw new Error(r.mensagem || 'Erro ao atualizar o plano.');
                aviso.ok('Plano atualizado.');
            } else {
                const r = await billingService.criarPlano({
                    nome: rascunho.nome.trim(),
                    descricao: rascunho.descricao.trim(),
                    preco: rascunho.preco,
                    ciclo: rascunho.ciclo,
                    limiteConexao: rascunho.limiteConexao,
                    integradoGateway: rascunho.integradoGateway,
                    ehPlanoTeste: rascunho.ehPlanoTeste,
                    provedorPagamento: rascunho.provedorPagamento,
                });
                if (!r.sucesso) throw new Error(r.mensagem || 'Erro ao criar o plano.');
                aviso.ok('Plano criado.');
            }
            await carregar(); fechar();
        } catch (err: any) {
            aviso.erro(textoDoErro(err, 'Não foi possível salvar o plano.'));
        } finally {
            setSalvando(false);
        }
    };

    /* `alvo` opcional porque a remoção tem duas portas: a lixeira da linha
       (que manda o plano) e o rodapé do painel (que usa o que está em edição). */
    const excluir = async (alvo?: PlanoBackend) => {
        const id = alvo?.planoId ?? rascunho.planoId;
        const nome = alvo?.planoNome ?? rascunho.nome;
        if (!id) return;
        if (!confirm(`Remover o plano "${nome}"? Empresas assinantes podem ser afetadas.`)) return;
        setSalvando(true);
        try {
            const r = await billingService.removerPlano(id);
            if (!r.sucesso) throw new Error(r.mensagem || 'Erro ao remover o plano.');
            aviso.ok('Plano removido.');
            await carregar(); fechar();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível remover o plano.'));
        } finally {
            setSalvando(false);
        }
    };

    /** Campo de limite: vazio = ilimitado (`null`), que é o que a API espera. */
    const campoLimite = (
        id: string, rotulo: string, valor: number | null,
        setValor: (v: number | null) => void,
    ) => (
        <div className="rp-field">
            <label className="rp-label" htmlFor={id}>{rotulo}</label>
            <input id={id} className="rp-input" type="number" min={0}
                value={valor === null ? '' : valor}
                placeholder="Ilimitado"
                onChange={e => setValor(e.target.value === '' ? null : Math.max(0, Number(e.target.value)))} />
            <span className="pm-hint">Deixe vazio para ilimitado.</span>
        </div>
    );

    const COLUNAS = '1.6fr 1fr 1fr .9fr .9fr .8fr 78px';

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {planos.length === 0 ? 'Nenhum plano cadastrado' : 'Nada com esses filtros'}
            </div>
            <p className="lk-blank-text">
                {planos.length === 0
                    ? 'Os planos definem o preço por usuário, o ciclo de cobrança e o limite de conexões.'
                    : 'Ajuste a busca ou o filtro.'}
            </p>
            {planos.length === 0 && (
                <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                    <LuPlus size={14} /> Novo plano
                </button>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Planos"
                subtitle="Os planos que as empresas podem assinar."
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!isLoading && planos.length === 0 ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                            <LuPlus size={14} /> Novo plano
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar por nome ou descrição…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    {([['todos', 'Todos'], ['ativos', 'Ativos'], ['inativos', 'Inativos']] as [Filtro, string][])
                        .map(([id, rotulo]) => (
                            <button key={id} type="button"
                                className={`lk-pill ${filtro === id ? 'is-on' : ''}`}
                                onClick={() => setFiltro(id)}>
                                {rotulo} <b>{contagens[id]}</b>
                            </button>
                        ))}
                </div>

                {/* Celular: as sete colunas não cabem em 358px, então a linha vira
                    cartão — o plano, o que ele custa e em que pé está. Gateway e
                    limite de conexões ficam no painel, um toque adiante. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(p => (
                                    <ListCard
                                        key={p.planoId}
                                        titulo={
                                            <>
                                                {p.planoNome}
                                                {p.planoIsTrial && <span className="pm-tag">teste</span>}
                                            </>
                                        }
                                        apoio={`${brl(p.planoPreco)} por usuário`}
                                        estado={
                                            <>
                                                <span className={`rp-badge ${p.planoAtivo ? 'is-good' : 'is-lost'}`}>
                                                    {p.planoAtivo ? 'Ativo' : 'Inativo'}
                                                </span>
                                                <span>· {rotuloCiclo(p.planoCiclo)}</span>
                                            </>
                                        }
                                        selected={aberto?.planoId === p.planoId}
                                        label={`Abrir ${p.planoNome}`}
                                        onOpen={() => abrirVer(p)}
                                        acoes={
                                            <RowAction icon={<LuTrash2 size={15} />} label="Remover plano" danger
                                                onClick={() => excluir(p)} />
                                        }
                                    />
                                ))}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Plano</div>
                            <div className="lk-head">Preço / usuário</div>
                            <div className="lk-head">Ciclo</div>
                            <div className="lk-head">Gateway</div>
                            <div className="lk-head">Conexões</div>
                            <div className="lk-head">Status</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(p => (
                                    <ListRow key={p.planoId} columns={COLUNAS}
                                        selected={aberto?.planoId === p.planoId}>
                                        <span className="lk-cell lk-strong">
                                            <span>{p.planoNome}</span>
                                            {p.planoIsTrial && <span className="pm-tag">teste</span>}
                                        </span>
                                        <span className="lk-cell pm-num"><span>{brl(p.planoPreco)}</span></span>
                                        <span className="lk-cell lk-muted"><span>{rotuloCiclo(p.planoCiclo)}</span></span>
                                        <span className="lk-cell lk-muted"><span>{rotuloProvedor(p.planoProvedorPagamento)}</span></span>
                                        <span className="lk-cell pm-num lk-muted"><span>{limite(p.planoLimiteConexao)}</span></span>
                                        <span className="lk-cell">
                                            <span className={`rp-badge ${p.planoAtivo ? 'is-good' : 'is-lost'}`}>
                                                {p.planoAtivo ? 'Ativo' : 'Inativo'}
                                            </span>
                                        </span>
                                        <RowActions>
                                            <RowAction icon={<LuEye size={14} />} label="Ver plano"
                                                onClick={() => abrirVer(p)} />
                                            <RowAction icon={<LuTrash2 size={14} />} label="Remover plano" danger
                                                onClick={() => excluir(p)} />
                                        </RowActions>
                                    </ListRow>
                                ))}
                        </div>
                    </div>

                    {!isLoading && filtrados.length === 0 && listaVazia}
                    {!isLoading && filtrados.length > 0 && rodape}
                </div>
                )}
            </div>

            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                title={modo === 'editar'
                    ? (rascunho.nome.trim() || (ehNovo ? 'Novo plano' : 'Plano'))
                    : (aberto?.planoNome ?? '')}
                subtitle={aberto && aberto.planoId
                    ? `${brl(aberto.planoPreco)} por usuário · ${rotuloCiclo(aberto.planoCiclo)}`
                    : undefined}
                editing={modo === 'editar'}
                onBack={ehNovo ? undefined : () => setModo('ver')}
                actions={aberto && aberto.planoId && (
                    <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                        <LuPencil size={13} /> Editar
                    </button>
                )}
                footer={
                    <>
                        {!ehNovo && (
                            <button type="button" className="rp-btn is-danger" onClick={() => excluir()} disabled={salvando}>
                                Remover
                            </button>
                        )}
                        <button type="button" className="rp-btn" disabled={salvando}
                            onClick={() => (ehNovo ? fechar() : setModo('ver'))}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                }
            >
                {modo === 'ver' && aberto && aberto.planoId && (
                    <>
                        <PanelSection title="Cobrança">
                            <div className="rp-kv">
                                <span className="rp-k">Preço por usuário</span>
                                <span className="rp-v">{brl(aberto.planoPreco)}</span>
                                <span className="rp-k">Ciclo</span>
                                <span className="rp-v">{rotuloCiclo(aberto.planoCiclo)}</span>
                                <span className="rp-k">Status</span>
                                <span className="rp-v">{aberto.planoAtivo ? 'Ativo' : 'Inativo'}</span>
                                <span className="rp-k">Plano de teste</span>
                                <span className="rp-v">{aberto.planoIsTrial ? 'Sim' : 'Não'}</span>
                            </div>
                        </PanelSection>

                        <PanelSection title="Limites">
                            <div className="rp-kv">
                                <span className="rp-k">Conexões</span>
                                <span className="rp-v">{limite(aberto.planoLimiteConexao)}</span>
                            </div>
                        </PanelSection>

                        <PanelSection title="Descrição">
                            {aberto.planoDescricao
                                ? <p className="pm-desc">{aberto.planoDescricao}</p>
                                : <p className="rp-none">Sem descrição.</p>}
                        </PanelSection>

                        <PanelSection title="Cobrança automática">
                            {/* O provedor é por plano desde o multi-gateway, então o
                                rótulo vem do dado — fixá-lo em "AbacatePay" fazia a
                                tela mentir sobre plano Asaas. */}
                            <div className="rp-kv">
                                <span className="rp-k">Gateway</span>
                                <span className="rp-v">
                                    {rotuloProvedor(aberto.planoProvedorPagamento)}
                                    {aberto.planoIntegradoGateway ? ' · integrado' : ' · não integrado'}
                                </span>
                                <span className="rp-k">ID do produto</span>
                                <span className={`rp-v ${aberto.gatewayProductId ? 'pm-mono' : 'is-none'}`}>
                                    {aberto.gatewayProductId || 'não gerado'}
                                </span>
                            </div>
                            {/* Só vale fora da Asaas: lá o identificador é
                                SINTETIZADO na hora do checkout
                                (`MontarIdentificadorGateway`), então id vazio
                                não impede contratar. */}
                            {aberto.planoIntegradoGateway
                              && aberto.planoProvedorPagamento !== 'ASAAS'
                              && !aberto.gatewayProductId && (
                                <p className="pm-alerta">
                                    <LuInfo size={13} />
                                    Sem o ID do produto a contratação falha com “plano ainda não
                                    sincronizado”. Este plano ainda não pode ser contratado.
                                </p>
                            )}
                        </PanelSection>
                    </>
                )}

                {modo === 'editar' && (
                    <>
                        <PanelSection title="Plano">
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="pm-nome">Nome</label>
                                <input id="pm-nome" className="rp-input" type="text" autoFocus
                                    value={rascunho.nome}
                                    onChange={e => { setRascunho(r => ({ ...r, nome: e.target.value })); setErros({}); }} />
                                {erros.nome && <span className="rp-error">{erros.nome}</span>}
                            </div>

                            <div className="rp-field">
                                <label className="rp-label" htmlFor="pm-desc">Descrição</label>
                                <input id="pm-desc" className="rp-input" type="text"
                                    value={rascunho.descricao}
                                    onChange={e => setRascunho(r => ({ ...r, descricao: e.target.value }))} />
                            </div>
                        </PanelSection>

                        <PanelSection title="Cobrança">
                            <div className="rp-two">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="pm-preco">Preço por usuário</label>
                                    <input id="pm-preco" className="rp-input" type="text" inputMode="numeric"
                                        value={precoTexto} placeholder="R$ 0,00"
                                        onChange={e => {
                                            const v = moedaDoTexto(e.target.value);
                                            setPrecoTexto(brl(v));
                                            setRascunho(r => ({ ...r, preco: v }));
                                        }} />
                                    <span className="pm-hint">O cliente paga este valor × usuários contratados.</span>
                                </div>

                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="pm-ciclo">Ciclo de cobrança</label>
                                    {ehNovo ? (
                                        <Select id="pm-ciclo" value={rascunho.ciclo}
                                            options={CICLOS.map(c => ({ value: c.valor, label: c.rotulo }))}
                                            onChange={v => setRascunho(r => ({ ...r, ciclo: v }))} />
                                    ) : (
                                        <>
                                            <p className="pm-fixo">{rotuloCiclo(rascunho.ciclo)}</p>
                                            <span className="pm-hint">
                                                Definido na criação e não muda — a API de atualização não recebe o ciclo.
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="rp-field">
                                <label className="rp-label" htmlFor="pm-provedor">Gateway de pagamento</label>
                                {ehNovo ? (
                                    <Select id="pm-provedor" value={rascunho.provedorPagamento}
                                        options={PROVEDORES}
                                        onChange={v => setRascunho(r => ({ ...r, provedorPagamento: v as ProvedorPagamento }))} />
                                ) : (
                                    <>
                                        <p className="pm-fixo">{rotuloProvedor(rascunho.provedorPagamento)}</p>
                                        <span className="pm-hint">
                                            Definido na criação — assinaturas ativas continuam no gateway em que foram contratadas.
                                        </span>
                                    </>
                                )}
                            </div>

                            {!ehNovo && (
                                <div className="rp-toggle-row">
                                    <div className="rp-toggle-text">
                                        <span className="rp-toggle-label">Plano ativo</span>
                                        <span className="rp-toggle-hint">Inativo, ele some da lista de assinatura.</span>
                                    </div>
                                    <Switch
                                        checked={rascunho.ativo}
                                        label="Plano ativo"
                                        onChange={v => setRascunho(r => ({ ...r, ativo: v }))}
                                    />
                                </div>
                            )}
                        </PanelSection>

                        <PanelSection title="Limites">
                            {campoLimite('pm-lc', 'Conexões por plataforma', rascunho.limiteConexao,
                                v => setRascunho(r => ({ ...r, limiteConexao: v })))}
                        </PanelSection>

                        {ehNovo && (
                            <PanelSection title="Criação">
                                <div className="rp-toggle-row">
                                    <div className="rp-toggle-text">
                                        <span className="rp-toggle-label">Plano de teste</span>
                                        <span className="rp-toggle-hint">Usado para o período de avaliação.</span>
                                    </div>
                                    <Switch
                                        checked={rascunho.ehPlanoTeste}
                                        label="Plano de teste"
                                        onChange={v => setRascunho(r => ({ ...r, ehPlanoTeste: v }))}
                                    />
                                </div>

                                <div className="rp-toggle-row">
                                    <div className="rp-toggle-text">
                                        <span className="rp-toggle-label">
                                            Integrar ao {rotuloProvedor(rascunho.provedorPagamento)}
                                        </span>
                                        <span className="rp-toggle-hint">Cria o produto na cobrança automática.</span>
                                    </div>
                                    <Switch
                                        checked={rascunho.integradoGateway}
                                        label={`Integrar ao ${rotuloProvedor(rascunho.provedorPagamento)}`}
                                        onChange={v => setRascunho(r => ({ ...r, integradoGateway: v }))}
                                    />
                                </div>

                                <p className="pm-alerta">
                                    <LuInfo size={13} />
                                    Ciclo, gateway, plano de teste e integração são definidos agora e não mudam depois.
                                </p>
                            </PanelSection>
                        )}
                    </>
                )}

                {/* Histórico (fase 6): plano é do sistema. */}
                {modo === 'ver' && aberto?.planoId ? (
                    <SecaoHistorico entidade="plano" id={aberto.planoId} />
                ) : null}
            </RecordPanel>
        </div>
    );
};

export default PlanManagementPage;
